import React from 'react';
import { Dropdown } from 'react-bootstrap';
import messagesCountQuery from '@aurora/shared-client/components/messages/MessagesCount.query.graphql';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import type {
  IdeaStatusDetails,
  MessageConstraints
} from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextNodeFragment,
  MessagesCountQuery,
  MessagesCountQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import useTranslation from '../../../useTranslation';

interface Props {
  /**
   * The idea status item to render
   */
  item: IdeaStatusDetails;
  /**
   * The node which filtering should be scoped to.
   */
  contextNode: ContextNodeFragment;
  /**
   * Whether this item is the active selected item
   */
  active: boolean;
  /**
   * Callback function when the item is selected.
   *
   * @callback
   */
  onStatusSelected?: (item: IdeaStatusDetails) => void;
}

/**
 * Single dropdown item for the idea status filter.
 * A query is run to find out how many idea messages are mapped to this status.
 * Only if the status has one or more messages, it will be displayed.
 *
 * @author rishabh.jivan
 */
const IdeaMessagesStatusFilterDropdownItem = ({
  item,
  active,
  contextNode,
  onStatusSelected
}: Props): React.ReactElement => {
  const i18n = useTranslation(EndUserComponent.IDEA_MESSAGES_STATUS_FILTER);
  const constraints: MessageConstraints = {
    boardId: { eq: contextNode.id },
    statusKey: { eq: item.statusKey },
    depth: { eq: 0 }
  };
  const { data: messageCountResult, loading: statusLoading } = useQueryWithTracing<
    MessagesCountQuery,
    MessagesCountQueryVariables
  >(module, messagesCountQuery, {
    variables: {
      constraints
    },
    skip: !item.statusKey
  });

  if (item.statusKey && statusLoading) {
    return null;
  }

  const isDisabled: boolean = item.statusKey && messageCountResult?.messagesCount === 0;

  return (
    <div title={isDisabled ? i18n.formatMessage('disabledTootipText') : null}>
      <Dropdown.Item
        active={active}
        key={item.statusKey}
        onSelect={(): void => onStatusSelected(item)}
        disabled={isDisabled}
      >
        <span aria-label={item.text}>{item.text}</span>
      </Dropdown.Item>
    </div>
  );
};

export default IdeaMessagesStatusFilterDropdownItem;
